import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import {
  FOCUS_RING_BRAND_SHADOW_SM,
  FOCUS_RING_ERROR_SHADOW_XS,
} from '../styles/common';
import { cn } from '../utils';

const inputVariants = cva(
  [
    'flex w-full rounded-md border bg-primary shadow-xs transition placeholder:text-placeholder disabled:cursor-not-allowed disabled:opacity-50 text-primary',
    // Dark mode
    'dark:focus-visible:border-brand-700',
    // File
    'file:border-0 file:bg-transparent file:font-medium',
    // Focus (default)
    'border-primary focus-visible:border-brand-300',
    FOCUS_RING_BRAND_SHADOW_SM,
  ],
  {
    variants: {
      inputSize: {
        default: 'h-10 px-3.5 py-2.5 text-md',
        lg: 'h-11 px-4 py-3 text-lg',
      },
      destructive: {
        true: [
          'border-error-500',
          FOCUS_RING_ERROR_SHADOW_XS,
          'focus-visible:border-error-500',
        ],
      },
      readOnlyStyle: {
        true: 'bg-secondary text-quaternary',
      },
    },
    defaultVariants: {
      inputSize: 'default',
      destructive: false,
      readOnlyStyle: false,
    },
  },
);

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement>,
    VariantProps<typeof inputVariants> {}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, inputSize, destructive, readOnly, ...props }, ref) => {
    const readOnlyStyle = readOnly;
    return (
      <input
        className={cn(
          inputVariants({ inputSize, destructive, readOnlyStyle, className }),
        )}
        ref={ref}
        type={type}
        readOnly={readOnly}
        {...props}
      />
    );
  },
);
Input.displayName = 'Input';
